/* Account-cleanup panel — iter-26 Phase D.
 * Lives in the Database tab; manual trigger + pending/archived lists.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Trash2, Archive, RefreshCw, Mail, RotateCcw } from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function AccountCleanupPanel() {
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [p, q] = await Promise.all([
        http.get('/admin/account-cleanup/preview').then(r => r.data),
        http.get('/admin/account-cleanup/pending').then(r => r.data),
      ]);
      setPreview(p);
      setPending(q);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load'); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const runPass = async () => {
    if (!(await openConfirm({
      title: 'Run cleanup pass now?',
      description: 'Warnings will be emailed, expired warnings will be archived, and stale archives will be purged.',
      confirmLabel: 'Run cleanup',
    }))) return;
    setBusy(true);
    try {
      const r = await http.post('/admin/account-cleanup/run').then(r => r.data);
      toast.success(`Cleanup done — warned ${r.warned}, archived ${r.archived}, purged ${r.purged}${r.delivery_failures.length ? `, ${r.delivery_failures.length} delivery failures` : ''}.`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run failed'); }
    finally { setBusy(false); }
  };

  const restore = async (uid) => {
    try {
      await http.post(`/admin/account-cleanup/users/${uid}/restore`);
      toast.success('Restored.');
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };

  if (!preview || !pending) return <div className="text-[12px] text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…</div>;

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="account-cleanup-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[12.5px] font-semibold">Inactive-account cleanup</div>
          <p className="text-[10.5px] text-muted-foreground">
            ≥365d inactive → warning email · +30d no reply → archive · +50d in archive → hard delete. Daily auto-pass runs at server start + every 24h.
          </p>
        </div>
        <div className="flex gap-1.5">
          <button onClick={reload} className="inline-flex h-7 items-center gap-1 rounded-sm bg-secondary px-2 text-[11.5px] hover:bg-secondary/80"
                  data-testid="cleanup-refresh">
            <RefreshCw className="h-3 w-3" /> Reload
          </button>
          <button onClick={runPass} disabled={busy} data-testid="cleanup-run"
                  className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />} Run cleanup pass
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-sm border border-border bg-background p-3">
          <div className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
            <Mail className="h-3 w-3" /> Will warn next pass
          </div>
          <div className="mt-1 text-2xl font-semibold" data-testid="cleanup-count-warn">{preview.candidates_warn.length}</div>
          <div className="mt-1 max-h-32 overflow-y-auto text-[10.5px] text-muted-foreground">
            {preview.candidates_warn.slice(0, 30).map((e) => <div key={`warn-${e}`}>{e}</div>)}
          </div>
        </div>
        <div className="rounded-sm border border-border bg-background p-3">
          <div className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
            <Archive className="h-3 w-3" /> Will archive next pass
          </div>
          <div className="mt-1 text-2xl font-semibold" data-testid="cleanup-count-archive">{preview.candidates_archive.length}</div>
          <div className="mt-1 max-h-32 overflow-y-auto text-[10.5px] text-muted-foreground">
            {preview.candidates_archive.slice(0, 30).map((e) => <div key={`arch-${e}`}>{e}</div>)}
          </div>
        </div>
        <div className="rounded-sm border border-border bg-background p-3">
          <div className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
            <Trash2 className="h-3 w-3" /> Will purge next pass
          </div>
          <div className="mt-1 text-2xl font-semibold text-destructive" data-testid="cleanup-count-purge">{preview.candidates_purge.length}</div>
          <div className="mt-1 max-h-32 overflow-y-auto text-[10.5px] text-muted-foreground">
            {preview.candidates_purge.slice(0, 30).map((e) => <div key={`purge-${e}`}>{e}</div>)}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Currently warned ({pending.warned.length})</div>
          <div className="overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-[11.5px]">
              <thead className="bg-panel-header/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left">Email</th>
                  <th className="px-2 py-1 text-left">Warned at</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {pending.warned.length === 0 && (
                  <tr><td colSpan={3} className="px-2 py-3 text-center text-muted-foreground">No pending warnings.</td></tr>
                )}
                {pending.warned.map((u) => (
                  <tr key={u.user_id} className="border-t border-border/60" data-testid={`cleanup-warned-${u.user_id}`}>
                    <td className="px-2 py-1">{u.email}{u.delivery_error && <div className="text-[10px] text-warning">{u.delivery_error}</div>}</td>
                    <td className="px-2 py-1 text-muted-foreground">{(u.warned_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => restore(u.user_id)} className="inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 text-[10.5px] hover:bg-secondary/80">
                        <RotateCcw className="h-2.5 w-2.5" /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="mb-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Archived ({pending.archived.length})</div>
          <div className="overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-[11.5px]">
              <thead className="bg-panel-header/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left">Email</th>
                  <th className="px-2 py-1 text-left">Archived at</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {pending.archived.length === 0 && (
                  <tr><td colSpan={3} className="px-2 py-3 text-center text-muted-foreground">No archived users.</td></tr>
                )}
                {pending.archived.map((u) => (
                  <tr key={u.user_id} className="border-t border-border/60" data-testid={`cleanup-archived-${u.user_id}`}>
                    <td className="px-2 py-1">{u.email}</td>
                    <td className="px-2 py-1 text-muted-foreground">{(u.archived_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => restore(u.user_id)} className="inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 text-[10.5px] hover:bg-secondary/80">
                        <RotateCcw className="h-2.5 w-2.5" /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
